import React from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { propTypes as authPropTypes, defaultProps as authDefaultProps } from 'shared/proptypes/Auth';
import { userportal as routes, BLOG_URL } from 'shared/routes';
import { ALL_JOBS_LINK } from 'shared/constants';
import Avatar from 'shared/components/Avatar';
import Badge from 'shared/components/Badge';
import IconBadge from 'shared/components/IconBadge';
import Logo from 'shared/components/Logo';
import LanguageSwitch from 'shared/components/LanguageSwitch';
import styles from './Mobile.module.scss';

const Mobile = ({
  loggedIn,
  user,
  logout,
  unreadApplicationChangesCount,
  unreadOfferChangesCount,
  hasInsufficientProfile,
  onToggleMenu,
  opened,
  onChangeLanguage,
  language
}) => {
  const { t } = useTranslation('userportal');
  const navigate = useNavigate();

  const handleMenuOpen = (event) => {
    if (event.target.id === 'select_flag_button' || event.target.className === 'flag-select__option__icon') {
      return;
    }

    if (loggedIn === '') {
      navigate(routes.login);
      return;
    }
    onToggleMenu(!opened);
  };

  const totalChanges = unreadOfferChangesCount + unreadApplicationChangesCount;

  return (
    <div className={styles.mobile}>
      <div className="container">
        <div className="row">
          <div className={`col-12 ${styles.navigation}`}>

            <div className={styles.logoCol}>
              <Link to={routes.root} className={styles.logo}>
                <Logo />
              </Link>
              <div className={styles.name}>
                <strong><Link to={routes.root}>{t('common:name')}</Link></strong>
                <p><Link to={routes.root}>{t('common:motto')}</Link></p>
              </div>
            </div>

            <div className={styles.rightCol}>
              <div onClick={handleMenuOpen} className={cn(styles.opener, { [styles.active]: !opened })}>
                {totalChanges > 0 ? (
                  <Badge highlighted position="right" content={totalChanges}>
                    <span className={styles.openerText}>{t('navigation.menuOpener')}</span>
                  </Badge>
                ) : (
                  <span className={styles.openerText}>{t('navigation.menuOpener')}</span>
                )}
                <span className={cn(styles.openerIcon, { ml8: totalChanges > 0 })}>
                  {loggedIn
                    ? <Avatar user={user} transparent className={styles.avatar} />
                    : <img src="/images/menu-open.png" className={styles.openCloseIcon} alt="" width="26" height="19" />
                  }
                  {hasInsufficientProfile && (
                    <IconBadge tooltipPosition="bottom" className={styles.warnBadge}>
                      {t('navigation.insufficientProfile')}
                    </IconBadge>
                  )}
                </span>
              </div>
              <div onClick={handleMenuOpen} className={cn(styles.opener, { [styles.active]: opened })}>
                <span>{t('navigation.closeMenu')}</span>
                <img src="/images/menu-close.png" className={styles.openCloseIcon} alt="" width="19" height="19" />
              </div>
            </div>

          </div>
        </div>
      </div>


      <div className={cn(styles.menu, { [styles.active]: opened })}>
        <div onClick={handleMenuOpen} className={styles.content}>
          <div className={styles.auth}>
            {!loggedIn && (
              <>
                <Link to={routes.login}>
                  <i className="la la-key" />
                  {t('navigation.login')}
                </Link>
                <span className={styles.spacer}>|</span>
                <Link to={routes.signup}>
                  <i className="la la-user-plus" />
                  {t('navigation.signup')}
                </Link>
              </>
            )}

            {loggedIn && (
              <>
                <Link to={routes.user.profile.view}>
                  <span>{user.firstname} {user.lastname}</span>
                </Link>
                <span className={styles.spacer}>|</span>
                <Link to={routes.root} onClick={logout} className={styles.logout}>
                  <i className="la la-sign-out-alt" />
                  {t('navigation.logout')}
                </Link>
              </>
            )}
            <LanguageSwitch
              language={language}
              onChange={onChangeLanguage}
              className={styles.langSwitch}
            />
          </div>

          <ul className={styles.items}>
            <li>
              <Link to={ALL_JOBS_LINK} rel="nofollow">
                <i className="la la-list-ol" />
                {t('intro.allJobs')}
              </Link>
            </li>
            {loggedIn && (
              <>
                <li>
                  <Link to={routes.user.favoriteJobs}>
                    <i className="lar la-heart" />
                    {t('navigation.favoriteJobsMobile')}
                  </Link>
                </li>
                <li>
                  <Link to={routes.user.offers}>
                    <i className="la la-sms" />
                    {unreadOfferChangesCount > 0 ? (
                      <Badge highlighted position="right" content={unreadOfferChangesCount}>
                        {t('navigation.myOffers')}
                      </Badge>
                    ) : (
                      <span>{t('navigation.myOffers')}</span>
                    )}
                  </Link>
                </li>
                <li>
                  <Link to={routes.user.applications}>
                    <i className="la la-clipboard-check" />
                    {unreadApplicationChangesCount > 0 ? (
                      <Badge highlighted position="right" content={unreadApplicationChangesCount}>
                        {t('navigation.myPositions')}
                      </Badge>
                    ) : (
                      <span>{t('navigation.myPositions')}</span>
                    )}
                  </Link>
                </li>
                <li>
                  <Link to={routes.user.profile.view} className="relative">
                    <i className="la la-user" />
                    <span>{t('navigation.myProfile')}</span>
                    {hasInsufficientProfile && <IconBadge position="right" tooltipPosition="bottom">{t('navigation.insufficientProfile')}</IconBadge>}
                  </Link>
                  <ul>
                    <li>
                      <Link to={`${routes.user.profile.view}#info`}>
                        <i className="la la-info-circle" />
                        {t('user.menu.generalInfo')}
                      </Link>
                    </li>
                    <li>
                      <Link to={`${routes.user.profile.view}#roles`}>
                        <i className="la la-user-tag" />
                        {t('user.menu.rolesSkills')}
                      </Link>
                    </li>
                    <li>
                      <Link to={`${routes.user.profile.view}#languages`}>
                        <i className="la la-globe" />
                        {t('user.menu.languages')}
                      </Link>
                    </li>
                    <li>
                      <Link to={`${routes.user.profile.view}#work`}>
                        <i className="la la-briefcase" />
                        {t('user.menu.works')}
                      </Link>
                    </li>
                    <li>
                      <Link to={`${routes.user.profile.view}#education`}>
                        <i className="la la-graduation-cap" />
                        {t('user.menu.edu')}
                      </Link>
                    </li>
                    <li>
                      <Link to={`${routes.user.profile.view}#preferences`}>
                        <i className="la la-clipboard-list" />
                        {t('user.menu.preferences')}
                      </Link>
                    </li>
                    <li>
                      <Link to={routes.user.profile.imports}>
                        <i className="la la-sync" />
                        {t('user.menu.imports')}
                      </Link>
                    </li>
                  </ul>
                </li>
                <li>
                  <Link to={routes.user.settings}>
                    <i className="la la-tools" />
                    {t('user.menu.settings')}
                  </Link>
                </li>
                <li className={styles.divider}><hr /></li>
              </>
            )}
            <li>
              <Link to={routes.about}>
                <i className="la la-info-circle" />
                {t('footer.about', { name: t('common:name') })}
              </Link>
            </li>
            <li>
              <Link to={routes.howItWorks}>
                <i className="la la-question-circle" />
                {t('footer.howItWorks')}
              </Link>
            </li>
            <li>
              <Link to={routes.tos}>
                <i className="la la-file-alt" />
                {t('footer.tos')}
              </Link>
            </li>
            <li>
              <Link to={routes.gdpr}>
                <i className="la la-file-alt" />
                {t('footer.gdpr')}
              </Link>
            </li>
            <li>
              <a href={BLOG_URL} target="_blank" rel="noopener noreferrer">
                <i className="la la-sms" />
                {t('footer.blog')}
              </a>
            </li>
            <li>
              <Link to={routes.contact}>
                <i className="la la-address-card" />
                {t('footer.contact')}
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

Mobile.propTypes = {
  loggedIn: authPropTypes.loggedIn,
  user: authPropTypes.user,
  logout: PropTypes.func,
  unreadApplicationChangesCount: PropTypes.number,
  unreadOfferChangesCount: PropTypes.number,
  hasInsufficientProfile: PropTypes.bool,
  onToggleMenu: PropTypes.func,
  opened: PropTypes.bool,
  onChangeLanguage: PropTypes.func,
  language: authPropTypes.language,
};

Mobile.defaultProps = {
  loggedIn: false,
  user: authDefaultProps.user,
  logout() {},
  unreadApplicationChangesCount: 0,
  unreadOfferChangesCount: 0,
  hasInsufficientProfile: false,
  onToggleMenu() {},
  opened: false,
  onChangeLanguage() {},
  language: authDefaultProps.language
};

export default Mobile;
